import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import deTranslations from '@/config/translations/de.json';
import enTranslations from '@/config/translations/en.json';
import { translatePathToEn, translatePathToDe } from '@/config/pathMap';

export type Language = 'de' | 'en';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TranslationValue = any;
type Translations = Record<string, TranslationValue>;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  tArray: (key: string) => string[];
  tObject: <T>(key: string) => T;
  translations: Translations;
  localizePath: (path: string) => string;
}

const translationsMap: Record<Language, Translations> = {
  de: deTranslations,
  en: enTranslations,
};

export function detectLanguageFromPath(pathname: string): Language {
  return pathname.startsWith('/en/') || pathname === '/en' ? 'en' : 'de';
}

export function stripLangPrefix(pathname: string): string {
  if (pathname === '/en') return '/';
  if (pathname.startsWith('/en/')) {
    const withoutPrefix = pathname.slice(3);
    return translatePathToDe(withoutPrefix);
  }
  return pathname;
}

export function addLangPrefix(pathname: string, lang: Language): string {
  if (!pathname.startsWith('/')) return pathname;
  const basePath = stripLangPrefix(pathname);
  if (lang === 'de') return basePath;
  const translatedPath = translatePathToEn(basePath);
  if (translatedPath === '/') return '/en';
  return `/en${translatedPath}`;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function getNestedValue(key: string, language: Language): TranslationValue {
  const keys = key.split('.');
  let value: TranslationValue = translationsMap[language];
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      return key;
    }
  }
  return value;
}

function tFn(language: Language) {
  return (key: string, params?: Record<string, string | number>): string => {
    const value = getNestedValue(key, language);
    if (typeof value !== 'string') return key;
    if (params) {
      return value.replace(/\{(\w+)\}/g, (_, paramKey) => String(params[paramKey] ?? `{${paramKey}}`));
    }
    return value;
  };
}

function tArrayFn(language: Language) {
  return (key: string): string[] => {
    const value = getNestedValue(key, language);
    return Array.isArray(value) ? value : [];
  };
}

function tObjectFn(language: Language) {
  return <T,>(key: string): T => getNestedValue(key, language) as T;
}

/**
 * SSG-safe provider: React Router exposes the current route during both
 * pre-rendering and hydration, so the server and client render the same locale.
 */
export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [language, setLanguageState] = useState<Language>(() => detectLanguageFromPath(location.pathname));

  useEffect(() => {
    const detectedLanguage = detectLanguageFromPath(location.pathname);
    setLanguageState(detectedLanguage);
    document.documentElement.lang = detectedLanguage;
    localStorage.setItem('chathero-language', detectedLanguage);
  }, [location.pathname]);

  const localizePath = useCallback((path: string) => {
    if (!path.startsWith('/')) return path;
    return addLangPrefix(path, language);
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    const currentBasePath = stripLangPrefix(location.pathname);
    const nextPath = addLangPrefix(currentBasePath, lang);

    setLanguageState(lang);
    document.documentElement.lang = lang;
    localStorage.setItem('chathero-language', lang);

    if (nextPath !== location.pathname) {
      navigate(`${nextPath}${location.search}${location.hash}`);
    }
  }, [location.hash, location.pathname, location.search, navigate]);

  const t = useCallback(tFn(language), [language]);
  const tArray = useCallback(tArrayFn(language), [language]);
  const tObject = useCallback(tObjectFn(language), [language]);

  const contextValue = useMemo(() => ({
    language, setLanguage, t, tArray, tObject, translations: translationsMap[language], localizePath
  }), [language, setLanguage, t, tArray, tObject, localizePath]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default useLanguage;
